/**
 * HISTÓRICO — auditoria append-only de decisão manual e automática.
 *
 * A coluna que importa é a origem. Quando o ator vem nulo, quem decidiu foi o
 * motor: escrever "—" ali transformaria decisão automática em "usuário
 * desconhecido", que é a leitura errada exatamente quando alguém está
 * procurando responsável por uma queda.
 *
 * Manual e automática ficam visualmente distintas de propósito: a pergunta
 * "isso foi alguém ou foi o sistema?" é a primeira de toda apuração.
 */
import { useEffect, useMemo, useState } from "react";
import type { RespostaHistorico } from "@co/contracts";
import { api } from "../api.js";
import { Botao, Carregando, Painel as Cartao, Selo, Tabela } from "../ui/base.js";
import { ErroTela, useCarga } from "../ui/carga.js";
import { Filtros, LinhaVazia, Paginacao, classeFiltro } from "../ui/dados.js";
import { dataHora, num, relativo } from "../ui/formato.js";
import { ORIGEM } from "../ui/rotulos.js";

const LIMITE = 50;

export function Historico(): JSX.Element {
  const [acao, setAcao] = useState("");
  const [acaoFirme, setAcaoFirme] = useState("");
  const [origem, setOrigem] = useState("");
  const [desloc, setDesloc] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setAcaoFirme(acao.trim());
      setDesloc(0);
    }, 350);
    return () => clearTimeout(t);
  }, [acao]);

  const chave = `historico|${acaoFirme}|${origem}|${desloc}`;
  const { carga, recarregar } = useCarga<RespostaHistorico>(
    () =>
      api.historico({
        acao: acaoFirme || undefined,
        origem: origem || undefined,
        limite: LIMITE,
        desloc,
      }),
    chave,
  );

  const dados = carga.fase === "pronto" ? carga.dados : null;

  // A ação é texto livre no contrato — não há enum para montar um seletor.
  // As ações da página vira sugestão: ajuda a acertar o nome sem inventar uma
  // lista fechada que o servidor não garante.
  const sugestoes = useMemo(
    () => [...new Set((dados?.itens ?? []).map((i) => i.acao))].sort(),
    [dados],
  );

  const temFiltro = Boolean(acaoFirme || origem);

  return (
    <Cartao
      titulo="Histórico"
      dica="Toda alteração manual e toda decisão automática do motor"
      semCorpo
    >
      <div className="px-5 py-3 border-b border-linha">
        <Filtros>
          <input
            value={acao}
            onChange={(e) => setAcao(e.target.value)}
            list="acoes-conhecidas"
            placeholder="Filtrar por ação"
            aria-label="Filtrar por ação"
            className={`${classeFiltro} min-w-[220px]`}
          />
          <datalist id="acoes-conhecidas">
            {sugestoes.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <select
            aria-label="Filtrar por origem"
            value={origem}
            onChange={(e) => {
              setOrigem(e.target.value);
              setDesloc(0);
            }}
            className={classeFiltro}
          >
            <option value="">Manual e automática</option>
            <option value="manual">Só decisão manual</option>
            <option value="automatica">Só decisão do motor</option>
          </select>
          {temFiltro && (
            <Botao
              pequeno
              tipo="fantasma"
              onClick={() => {
                setAcao("");
                setAcaoFirme("");
                setOrigem("");
                setDesloc(0);
              }}
            >
              Limpar filtros
            </Botao>
          )}
          {dados && (
            <span className="text-[12px] text-tinta-3 ml-auto">
              {num(dados.pagina.total)} registro(s)
            </span>
          )}
        </Filtros>
      </div>

      {carga.fase === "carregando" && <Carregando />}
      {carga.fase === "erro" && (
        <div className="p-5">
          <ErroTela mensagem={carga.mensagem} aoTentar={recarregar} />
        </div>
      )}

      {dados && (
        <>
          <Tabela
            larguraMin={980}
            colunas={[
              { chave: "q", rotulo: "Quando" },
              { chave: "a", rotulo: "Ação" },
              { chave: "o", rotulo: "Objeto" },
              { chave: "r", rotulo: "Resumo" },
              { chave: "g", rotulo: "Origem" },
              { chave: "u", rotulo: "Quem decidiu" },
            ]}
          >
            {dados.itens.length === 0 ? (
              <LinhaVazia colunas={6}>
                {temFiltro
                  ? "Nenhum registro corresponde aos filtros."
                  : "Sem registros ainda. Toda decisão passa a aparecer aqui."}
              </LinhaVazia>
            ) : (
              dados.itens.map((e) => {
                const o = ORIGEM[e.origem];
                return (
                  <tr
                    key={e.id}
                    className="border-t border-linha align-top hover:bg-superficie-2"
                  >
                    <td className="px-5 py-2.5 text-[12px] tabular whitespace-nowrap">
                      {dataHora(e.ocorridoEm)}
                      <span className="block text-[11px] text-tinta-3">
                        {relativo(e.ocorridoEm)}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-[12.5px]">{e.acao}</td>
                    <td className="px-5 py-2.5 text-[12px] text-tinta-2">
                      {e.objetoTipo}
                      {e.objetoId && (
                        <span
                          className="block text-[11px] text-tinta-3 tabular truncate max-w-[160px]"
                          title={e.objetoId}
                        >
                          {e.objetoId}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-[12.5px] max-w-[360px]">
                      {e.resumo}
                    </td>
                    <td className="px-5 py-2.5">
                      <Selo tom={o.tom}>{o.rotulo}</Selo>
                    </td>
                    <td className="px-5 py-2.5 text-[12.5px]">
                      {e.atorNome ?? (
                        <span className="text-tinta-3">
                          motor — decisão automática
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </Tabela>
          <Paginacao pagina={dados.pagina} aoIr={setDesloc} />
        </>
      )}
    </Cartao>
  );
}
